import React from 'react';
import styled from 'styled-components';
import CheckBox from '../../../atoms/Checkbox/Checkbox';
import Flex from '../../../atoms/Box/Flex';
import FlagIcon from '../../../atoms/FlagIcon/FlagIcon';
import Text from '../../../atoms/Text/Text';
import {
    formatOnboardingCountry,
    onboardingCountries,
} from '../../../../constants/onboardingCountries';
import { salesSegments } from '../../../../types/onboarding/OnboardStore';

/**
 * The markets a merchant can be restricted to: the onboarding countries that POST organizations/{id}/merchants accepts
 * as an allowed market. EU is an onboarding country but not a country code the API accepts, so it cannot be picked.
 */
export const allowedMarketOptions = onboardingCountries.filter(country => country.code !== 'EU');

interface AllowedMarketsAndSegmentsProps {
    /** The selected market codes; empty when the merchant is not restricted. */
    markets: string[];
    /** The selected segments; empty when the merchant is not restricted. */
    segments: string[];
    onMarketsChange: (markets: string[]) => void;
    onSegmentsChange: (segments: string[]) => void;
    disabled?: boolean;
}

const toggle = (selected: string[], code: string, checked: boolean): string[] =>
    checked ? [...selected, code] : selected.filter(x => x !== code);

/**
 * Checkboxes for the markets and sales segments a merchant's stores may be onboarded in. Nothing checked in a group
 * means no restriction in that group: the request then leaves the field out and the merchant may onboard in all of
 * them.
 */
const AllowedMarketsAndSegments: React.FC<AllowedMarketsAndSegmentsProps> = ({
    markets,
    segments,
    onMarketsChange,
    onSegmentsChange,
    disabled,
}: AllowedMarketsAndSegmentsProps) => {
    return (
        <>
            <Section>
                <Text medium>Allowed markets</Text>
                <Options data-testid="allowed-markets">
                    {allowedMarketOptions.map(country => (
                        <CheckBox
                            key={country.code}
                            id={`allowedMarket-${country.code}`}
                            dataTestId={`allowed-market-${country.code}`}
                            checked={markets.includes(country.code)}
                            onChange={e =>
                                onMarketsChange(toggle(markets, country.code, e.target.checked))
                            }
                            disabled={disabled}
                            noMargin
                        >
                            <MarketLabel>
                                <FlagIcon countryCode={country.code} />
                                {formatOnboardingCountry(country)}
                            </MarketLabel>
                        </CheckBox>
                    ))}
                </Options>
            </Section>
            <Section>
                <Text medium>Allowed segments</Text>
                <Options data-testid="allowed-segments">
                    {salesSegments.map(segment => (
                        <CheckBox
                            key={segment.value}
                            id={`allowedSegment-${segment.value}`}
                            dataTestId={`allowed-segment-${segment.value}`}
                            checked={segments.includes(segment.value)}
                            onChange={e =>
                                onSegmentsChange(toggle(segments, segment.value, e.target.checked))
                            }
                            disabled={disabled}
                            noMargin
                        >
                            {segment.label} – {segment.description.toLowerCase()}
                        </CheckBox>
                    ))}
                </Options>
                <Text as="p" secondary small>
                    Stores can only be onboarded in the checked markets and segments. Leave a group
                    unchecked to allow all of them.
                </Text>
            </Section>
        </>
    );
};

/** One group: its label, the checkboxes and the hint, stacked close together. */
const Section = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.xsmall,
}))``;

/** The checkboxes of a group, side by side and wrapping onto more lines when the modal is narrow. */
const Options = styled(Flex).attrs(props => ({
    flexWrap: 'wrap',
    gap: `${props.theme.layout.gap.xsmall} ${props.theme.layout.gap.medium}`,
}))``;

/** The flag and the name of a market on one line. */
const MarketLabel = styled(Flex).attrs(props => ({
    alignItems: 'center',
    gap: props.theme.layout.gap.xsmall,
}))``;

export default AllowedMarketsAndSegments;
